import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  UsersIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import DownloadQrCode from '../components/help/DownloadQrCode';
import Skeleton from '../components/common/Skeleton';
import { useApiClient } from '../lib/api';

const quickStartSteps = [
  {
    step: 1,
    icon: BuildingStorefrontIcon,
    title: 'Cadastre seu restaurante',
    description: 'Crie a unidade, revise o fuso horário e habilite horários de funcionamento quando a operação exigir bloqueio fora do expediente.',
  },
  {
    step: 2,
    icon: MapPinIcon,
    title: 'Configure áreas e mesas',
    description: 'Organize salão, balcão, varanda ou outros setores antes de gerar os QR Codes.',
  },
  {
    step: 3,
    icon: QrCodeIcon,
    title: 'Imprima os QR Codes',
    description: 'Gere o QR individual ou em massa, teste a leitura e instale o material físico na mesa correta.',
  },
  {
    step: 4,
    icon: UsersIcon,
    title: 'Cadastre a equipe',
    description: 'Adicione administradores, gerentes e garçons com vínculo aos restaurantes onde atuam.',
  },
  {
    step: 5,
    icon: DevicePhoneMobileIcon,
    title: 'Instale o aplicativo',
    description: 'A equipe usa o aplicativo para receber chamadas em tempo real com alerta sonoro.',
  },
  {
    step: 6,
    icon: Squares2X2Icon,
    title: 'Acompanhe chamadas',
    description: 'Clientes escaneiam o QR Code, a chamada entra na central e a equipe assume, finaliza ou reabre atendimentos.',
  },
];

const faqItems = [
  {
    category: 'Operação',
    question: 'Como os clientes fazem uma chamada?',
    answer:
      'O cliente escaneia o QR Code impresso na mesa usando a câmera do celular. Uma página web abre automaticamente e envia o chamado para a equipe, sem necessidade de instalar aplicativo.',
  },
  {
    category: 'Conectividade',
    question: 'O aplicativo funciona sem internet?',
    answer:
      'O aplicativo requer conexão com internet para receber chamados em tempo real. Se a conexão cair, a equipe deve acompanhar a reconexão e atualizar a central quando necessário.',
  },
  {
    category: 'Restaurantes',
    question: 'Posso usar o sistema em mais de um restaurante?',
    answer:
      'Sim. Dependendo do plano, a empresa pode cadastrar múltiplos restaurantes. Cada restaurante possui suas próprias áreas, mesas, equipe e configuração de horário.',
  },
  {
    category: 'Assinatura',
    question: 'Como alterar meu plano ou cancelar a assinatura?',
    answer:
      'Acesse Minha Assinatura no menu lateral. Lá você visualiza plano atual, consumo de mesas e restaurantes, próxima cobrança e ações disponíveis para alteração ou cancelamento.',
  },
  {
    category: 'Acesso',
    question: 'Como redefinir minha senha?',
    answer:
      'Na tela de login, clique em Esqueceu a senha? e siga o processo de recuperação por email. Um código de verificação será enviado para confirmar sua identidade.',
  },
  {
    category: 'QR Code',
    question: 'Os QR Codes expiram?',
    answer:
      'Os QR Codes das mesas não expiram enquanto a mesa estiver ativa. Se a mesa for excluída e recriada, gere e imprima um novo QR Code para evitar vínculo incorreto.',
  },
  {
    category: 'Integrações',
    question: 'É possível integrar com outros sistemas?',
    answer:
      'Sim. O WaiterCall oferece API com credenciais HMAC. Acesse Integrações para criar credenciais, limitar escopo por restaurante e consultar as instruções de assinatura.',
  },
];

const versionComparisonRows = [
  { feature: 'Instalação', playStore: 'Pela loja oficial', sideload: 'Baixar e instalar APK manualmente' },
  { feature: 'Atualizações', playStore: 'Automáticas via Play Store', sideload: 'Manuais, com download de nova versão' },
  { feature: 'Verificação', playStore: 'Google Play Protect', sideload: 'Requer fonte desconhecida e liberação manual de permissões' },
  { feature: 'Notificações', playStore: 'Push convencional do Android', sideload: 'Alerta de alta prioridade, tela cheia e sobreposição quando habilitada' },
  { feature: 'Permissões extras', playStore: 'Notificações e bateria sem restrição', sideload: 'Notificações, sobreposição e bateria sem restrição' },
  { feature: 'Perfil operacional', playStore: 'Uso geral', sideload: 'Ambientes com ruído alto, como cozinhas e bares' },
];

const playStorePermissions = [
  'Ativar notificações do Orion WaiterCall.',
  'Evitar restrição de bateria para manter alertas em segundo plano.',
  'Abrir o app e fazer login uma vez para registrar o dispositivo.',
];

const sideloadPermissions = [
  'Permitir instalação de apps desconhecidos para instalar o APK.',
  'Ativar notificações do Orion WaiterCall.',
  'Permitir sobreposição a outros apps para o alerta reforçado.',
  'Remover restrição de bateria quando o Android oferecer essa opção.',
];

const sideloadSetupSteps = [
  'Instale o APK e abra o app pelo menos uma vez.',
  'No Android, abra Configurações, Apps, Orion WaiterCall e Informações do app.',
  'Toque nos três pontos no canto superior direito e selecione Permitir configurações restritas.',
  'Volte para Sobrepor a outros apps e habilite Permitir sobreposição a outros apps.',
  'Revise notificações e bateria antes de entregar o dispositivo para operação.',
];

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const HelpPage = () => {
  const api = useApiClient();
  const location = useLocation();
  const [helpData, setHelpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [faqSearch, setFaqSearch] = useState('');

  const loadHelp = useCallback(async () => {
    setError(false);
    try {
      const response = await api.get('/api/v1/help');
      setHelpData(response.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    document.title = 'Ajuda & Suporte | Orion WaiterCall';
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await loadHelp();
      if (!mounted) return;
    };
    run();
    return () => {
      mounted = false;
    };
  }, [loadHelp]);

  const support = helpData?.support;
  const downloads = helpData?.downloads;
  const hasPlayStoreDownload = Boolean(downloads?.playStoreUrl);
  const hasSideloadDownload = Boolean(downloads?.sideloadUrl);
  const hasDownloads = hasPlayStoreDownload || hasSideloadDownload;
  const hasMultipleDownloadOptions = hasPlayStoreDownload && hasSideloadDownload;
  const downloadSectionDescription = hasMultipleDownloadOptions
    ? 'Escolha a distribuição mais adequada ao ambiente do restaurante.'
    : hasSideloadDownload
      ? 'Instale a versão direta do aplicativo enquanto a publicação na Play Store não estiver disponível para a operação.'
      : 'Instale o aplicativo pela loja oficial quando a publicação estiver disponível.';

  useEffect(() => {
    if (loading || !hasDownloads || typeof window === 'undefined') {
      return undefined;
    }

    const targetId = location.hash === '#mobile-downloads' || location.hash === '#sideload-permissions'
      ? location.hash.slice(1)
      : null;

    if (!targetId) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const element = document.getElementById(targetId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      element.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [hasDownloads, loading, location.hash]);

  const filteredFaqItems = useMemo(() => {
    const term = normalizeText(faqSearch.trim());
    if (!term) return faqItems;

    return faqItems.filter((item) => normalizeText(`${item.category} ${item.question} ${item.answer}`).includes(term));
  }, [faqSearch]);

  return (
    <section className="space-y-8">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative bg-slate-950 px-5 py-7 text-white sm:px-6 lg:px-8">
          <div className="relative grid gap-6 lg:grid-cols-[1fr_280px] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-200">Central de ajuda</p>
              <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">Ajuda & Suporte</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
                Encontre orientações para configurar o WaiterCall, instalar o aplicativo, operar chamadas e acionar suporte com as informações certas.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-950">
                  <LifebuoyIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold">Suporte orientado</p>
                  <p className="mt-1 text-xs leading-5 text-slate-300">Revise os passos antes de abrir chamado para reduzir tempo de atendimento.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          eyebrow="Primeiros passos"
          title="Roteiro de implantação"
          description="Siga esta sequência para colocar o restaurante em operação com menos retrabalho."
        />
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {quickStartSteps.map((item) => <QuickStartCard key={item.step} item={item} />)}
        </div>
      </section>

      {!loading && hasDownloads ? (
        <section id="mobile-downloads" tabIndex={-1} className="scroll-mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm outline-none sm:p-6">
          <SectionHeader
            eyebrow="Aplicativo"
            title="Instalação mobile"
            description={downloadSectionDescription}
          />
          <div className={clsx('mt-5 grid gap-4', hasMultipleDownloadOptions ? 'lg:grid-cols-2' : 'max-w-3xl')}>
            {downloads.playStoreUrl ? (
              <DownloadCard
                icon={<PlayStoreIcon className="h-6 w-6" aria-hidden="true" />}
                title="Google Play Store"
                description="Versão oficial distribuída pela Google Play Store, com atualização automática e notificações padrão do Android."
                hint="Indicada para operação geral, manutenção simplificada e instalação pelos canais padrão do Android."
                permissionsTitle="Permissões após instalar"
                permissions={playStorePermissions}
                qrLabel="QR Code da Play Store"
                qrDescription="Escaneie com o dispositivo Android para abrir a loja oficial."
                href={downloads.playStoreUrl}
                action="Abrir na Play Store"
              />
            ) : null}

            {downloads.sideloadUrl ? (
              <DownloadCard
                icon={<DevicePhoneMobileIcon className="h-6 w-6" aria-hidden="true" />}
                title="Download direto (APK)"
                description="Versão para instalação manual com recursos operacionais reforçados, incluindo alerta em tela cheia e sobreposição quando habilitada."
                hint="Requer instalação por fonte desconhecida e liberação manual de permissões avançadas no Android."
                permissionsTitle="Permissões necessárias"
                permissions={sideloadPermissions}
                setupTitle="Configuração obrigatória para alerta reforçado"
                setupDescription="Se a sobreposição aparecer bloqueada, libere configurações restritas nas Informações do app antes de ativar a permissão."
                detailHref="#sideload-permissions"
                qrLabel="QR Code do APK"
                qrDescription="Escaneie com o dispositivo Android para baixar o APK direto."
                href={downloads.sideloadUrl}
                action="Baixar APK"
                tone="slate"
              />
            ) : null}
          </div>

          {hasSideloadDownload ? <SideloadPermissionsGuide /> : null}

          {hasMultipleDownloadOptions ? <VersionComparison /> : null}
        </section>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          eyebrow="Base de conhecimento"
          title="Perguntas frequentes"
          description="Busque por módulo ou abra os tópicos para revisar dúvidas operacionais comuns."
          actions={
            <div className="relative w-full sm:max-w-sm">
              <label htmlFor="faq-search" className="sr-only">Buscar pergunta frequente</label>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                id="faq-search"
                type="search"
                value={faqSearch}
                onChange={(event) => setFaqSearch(event.target.value)}
                placeholder="Buscar por módulo ou dúvida..."
                className="block w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          }
        />

        <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50/70 p-2 sm:p-3">
          {filteredFaqItems.length > 0 ? (
            <div className="space-y-2">
              {filteredFaqItems.map((item, index) => <FAQItem key={item.question} item={item} index={index} />)}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center">
              <BookOpenIcon className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
              <p className="mt-3 text-sm font-semibold text-slate-900">Nenhuma pergunta encontrada</p>
              <p className="mt-1 text-sm text-slate-500">Tente buscar por QR Code, equipe, assinatura, integração ou restaurante.</p>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          eyebrow="Atendimento"
          title="Contato & Suporte"
          description="Use os canais abaixo quando precisar de apoio da equipe responsável pelo sistema."
        />
        <SupportContent loading={loading} error={error} support={support} onRetry={() => { setLoading(true); loadHelp(); }} />
      </section>
    </section>
  );
};

function SectionHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{eyebrow}</p>
        <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
        {description ? <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">{description}</p> : null}
      </div>
      {actions ? <div className="w-full lg:w-auto">{actions}</div> : null}
    </div>
  );
}

function QuickStartCard({ item }) {
  const Icon = item.icon;
  return (
    <article className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/70 p-5 transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md">
      <div className="absolute right-0 top-0 h-20 w-20 translate-x-8 -translate-y-8 rounded-full bg-indigo-100/70 transition group-hover:bg-indigo-200/70" />
      <div className="relative flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-bold text-white shadow-sm">
          {item.step}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-950">{item.title}</h3>
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-600">{item.description}</p>
        </div>
      </div>
    </article>
  );
}

function DownloadCard({
  icon,
  title,
  description,
  hint,
  permissionsTitle,
  permissions = [],
  setupTitle,
  setupDescription,
  detailHref,
  qrLabel,
  qrDescription,
  href,
  action,
  tone = 'indigo',
}) {
  const isSlate = tone === 'slate';
  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/70 shadow-sm">
      <div className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="flex items-center gap-3">
          <span className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl', isSlate ? 'bg-slate-100 text-slate-700' : 'bg-indigo-50 text-indigo-600')}>
            {icon}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
          </div>
        </div>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm leading-6 text-slate-600">{description}</p>
        <p className="mt-2 text-xs leading-5 text-slate-500">{hint}</p>
      </div>
      {permissions.length ? (
        <div className="border-t border-slate-200 bg-white px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{permissionsTitle}</p>
          <ul className="mt-3 space-y-2">
            {permissions.map((item) => (
              <li key={item} className="flex gap-2 text-sm leading-6 text-slate-600">
                <CheckCircleIcon className={clsx('mt-0.5 h-4 w-4 shrink-0', isSlate ? 'text-slate-500' : 'text-indigo-600')} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {setupTitle ? (
        <div className="border-t border-slate-200 bg-slate-100/70 px-5 py-4">
          <p className="text-sm font-semibold text-slate-950">{setupTitle}</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">{setupDescription}</p>
          {detailHref ? (
            <a href={detailHref} className="mt-3 inline-flex text-sm font-semibold text-indigo-700 transition hover:text-indigo-600">
              Ver passo a passo de permissões
            </a>
          ) : null}
        </div>
      ) : null}
      <div className="border-t border-slate-200 bg-white px-5 py-4">
        <DownloadQrCode
          url={href}
          label={qrLabel ?? `QR Code - ${title}`}
          description={qrDescription}
          framed={false}
        />
      </div>
      <div className="border-t border-slate-200 bg-white px-5 py-4">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx('inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition', isSlate ? 'bg-slate-800 hover:bg-slate-700' : 'bg-indigo-600 hover:bg-indigo-500')}
        >
          {action}
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </article>
  );
}

function SideloadPermissionsGuide() {
  return (
    <div id="sideload-permissions" tabIndex={-1} className="mt-5 scroll-mt-6 rounded-3xl border border-slate-200 bg-slate-50/80 p-5 outline-none sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">APK manual</p>
          <h3 className="mt-2 text-base font-semibold tracking-tight text-slate-950">Permitir configurações restritas e sobreposição</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Alguns aparelhos Android bloqueiam permissões avançadas para apps instalados fora da Play Store. Se a permissão de sobreposição ficar desativada, libere configurações restritas antes de habilitar o alerta reforçado.
          </p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 lg:max-w-sm">
          Os nomes dos menus podem variar por fabricante, mas o caminho normalmente começa em Informações do app.
        </div>
      </div>

      <ol className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        {sideloadSetupSteps.map((step, index) => (
          <li key={step} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
              {index + 1}
            </span>
            <span className="text-sm leading-6 text-slate-600">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function VersionComparison() {
  return (
    <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200">
          <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-slate-950">Comparação entre versões</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">Use a Play Store para manutenção simplificada e o APK direto quando a operação exigir alerta de alta prioridade.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:hidden">
        {versionComparisonRows.map((row) => (
          <div key={row.feature} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-950">{row.feature}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ComparisonValue label="Play Store" value={row.playStore} />
              <ComparisonValue label="Download direto" value={row.sideload} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white lg:block">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Recurso</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Play Store</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Download direto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {versionComparisonRows.map((row) => (
              <tr key={row.feature}>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{row.feature}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.playStore}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.sideload}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComparisonValue({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm leading-6 text-slate-700">{value}</p>
    </div>
  );
}

function FAQItem({ item, index }) {
  return (
    <Disclosure as="div" className="rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-slate-300">
      {({ open }) => (
        <>
          <DisclosureButton
            className={clsx(
              'flex w-full items-start justify-between gap-4 rounded-2xl px-4 py-4 text-left transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:px-5',
              open ? 'bg-indigo-50/70' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex min-w-0 gap-3">
              <span className={clsx('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition', open ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500')}>
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0">
                <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {item.category}
                </span>
                <p className="mt-2 text-sm font-semibold leading-6 text-slate-950">{item.question}</p>
              </div>
            </div>
            <ChevronDownIcon className={clsx('mt-1 h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200', open && 'rotate-180 text-indigo-600')} aria-hidden="true" />
          </DisclosureButton>
          <DisclosurePanel className="px-4 pb-5 pt-0 sm:px-5">
            <div className="ml-10 border-l border-slate-200 pl-4 sm:ml-12">
              <p className="text-sm leading-7 text-slate-600">{item.answer}</p>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}

function SupportContent({ loading, error, support, onRetry }) {
  if (loading) {
    return (
      <div className="mt-5 grid gap-3 lg:grid-cols-3">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-5 rounded-3xl border border-rose-200 bg-rose-50 p-5">
        <div className="flex items-start gap-3">
          <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-rose-900">Erro ao carregar informações de suporte</p>
            <p className="mt-1 text-sm leading-6 text-rose-700">Não foi possível obter os dados de contato. Verifique sua conexão e tente novamente.</p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm ring-1 ring-inset ring-rose-300 transition hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (support?.email || support?.phone || support?.businessHours) {
    return (
      <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="grid gap-3 lg:grid-cols-3">
          {support.email ? (
            <SupportContactItem icon={EnvelopeIcon} label="Email">
              <a href={`mailto:${support.email}`} className="text-indigo-700 transition hover:text-indigo-600">{support.email}</a>
            </SupportContactItem>
          ) : null}
          {support.phone ? (
            <SupportContactItem icon={PhoneIcon} label="Telefone">
              <a href={`tel:${support.phone.replace(/\s/g, '')}`} className="text-indigo-700 transition hover:text-indigo-600">{support.phone}</a>
            </SupportContactItem>
          ) : null}
          {support.businessHours ? (
            <SupportContactItem icon={ClockIcon} label="Horário de atendimento" tone="slate">
              {support.businessHours}
            </SupportContactItem>
          ) : null}
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <p>Para agilizar o atendimento, informe a tela acessada, o usuário, a empresa ativa e uma descrição objetiva do problema.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
      <LifebuoyIcon className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
      <p className="mt-3 text-sm text-slate-500">As informações de contato do suporte não estão disponíveis no momento.</p>
    </div>
  );
}

function SupportContactItem({ icon: Icon, label, children, tone = 'indigo' }) {
  const isSlate = tone === 'slate';
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <span className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', isSlate ? 'bg-slate-100 text-slate-600' : 'bg-indigo-50 text-indigo-600')}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <div className="mt-1 break-words text-sm font-semibold text-slate-950">{children}</div>
      </div>
    </div>
  );
}

function PlayStoreIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M3.609 1.814 13.792 12 3.61 22.186A.996.996 0 0 1 3 21.266V2.734a1 1 0 0 1 .609-.92Zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635Zm3.199-3.199 2.773 1.604a1 1 0 0 1 0 1.776l-2.773 1.604-2.537-2.537 2.537-2.447ZM5.864 2.658 16.8 8.99l-2.302 2.302-8.634-8.634Z" />
    </svg>
  );
}

export default HelpPage;
